import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  X as XIcon, Save, AlertTriangle, Building2, MapPin, ShieldCheck, Info,
} from "lucide-react";

import {
  fetchMastersDimensions,
  fetchChannelPreview,
  patchEdition,
  patchChannel,
  type CityOption,
  type EditionRow,
  type ChannelPreview as ChannelPreviewT,
  type ChannelPatchBody,
} from "../api/masters";
import { explainError } from "../api/client";
import { Combobox, type ComboOption } from "./Combobox";
import { parseReviewReasons } from "../lib/reviewReasons";
import { shortDate } from "../lib/format";

/* ──────────────────────────────────────────────────────────────────────
   Side drawer for editing a single edition.

   Two tabs:
     • EDITION   — per-row fields: city / state / printed label, IsActive,
                   IsCrawlable, circulation, readership. Clear review flag.
     • CHANNEL   — channel-level fields: name, category, language. Changes
                   propagate to every edition row of that channel.

   City and state inputs use a Combobox sourced from /api/masters/dimensions
   (5-minute TanStack Query cache — effectively session-cached). Substring
   search: typing "de" surfaces Delhi, Dehradun, Deoria, etc.
   ────────────────────────────────────────────────────────────────────── */

interface Props {
  row: EditionRow;
  onClose: () => void;
  onSaved: () => void;
}

type Tab = "edition" | "channel";

export function EditionEditDrawer({ row, onClose, onSaved }: Props) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("edition");

  // Dimensions cached for the session via TanStack Query
  const dims = useQuery({
    queryKey: ["masters/dimensions"],
    queryFn:  fetchMastersDimensions,
    staleTime: 5 * 60_000,
  });

  const cityOptions: ComboOption[] = useMemo(() => {
    return (dims.data?.cities ?? []).map(c => ({
      value: c.city,
      label: c.city,
      hint:  c.state ?? undefined,
      count: c.edition_count,
    }));
  }, [dims.data]);

  // Lookup table for the city auto-fill: key by lowercased trimmed name so a
  // city pick can populate State / State_Id / Zone. If the master has dupe
  // names (Aizawl / Aizwal — actually different ids), the first one wins;
  // user can still hand-edit the State / Zone fields after auto-fill.
  const cityByKey = useMemo(() => {
    const m = new Map<string, CityOption>();
    for (const c of dims.data?.cities ?? []) {
      const k = c.city.trim().toLowerCase();
      if (!m.has(k)) m.set(k, c);
    }
    return m;
  }, [dims.data]);

  const stateOptions: ComboOption[] = useMemo(() => {
    return (dims.data?.states ?? []).map(s => ({
      value: s.state,
      label: s.state,
      count: s.edition_count,
    }));
  }, [dims.data]);

  // Lookup by state name → state_id, for sending the canonical foreign key
  // when the user picks a state from the master.
  const stateByKey = useMemo(() => {
    const m = new Map<string, number | null>();
    for (const s of dims.data?.states ?? []) {
      m.set(s.state.trim().toLowerCase(), s.state_id ?? null);
    }
    return m;
  }, [dims.data]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <aside
        className="relative w-full max-w-[520px] h-full overflow-auto np-card shadow-plate"
        style={{ background: "var(--bg-card)" }}
      >
        {/* Header */}
        <div
          className="px-5 py-3 border-b flex items-start justify-between sticky top-0 bg-[var(--bg-card)] z-10"
          style={{ borderColor: "var(--rule)" }}
        >
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)]">
              Edit · edition {row.edition_id}
            </div>
            <h2 className="font-display text-[20px] leading-tight truncate">
              {row.channel_name}
            </h2>
            <div className="font-mono text-[10px] text-[var(--fg-muted)] mt-0.5">
              {row.city ?? <span className="text-[var(--accent)]">no city</span>}
              {row.language ? ` · ${row.language}` : ""}
              {row.category ? ` · ${row.category}` : ""}
              {` · ch${row.channel_id}`}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" className="np-btn-ghost p-2 shrink-0">
            <XIcon size={13} />
          </button>
        </div>

        {/* Tabs */}
        <div
          className="px-5 pt-2 flex items-center gap-px border-b"
          style={{ borderColor: "var(--rule)" }}
        >
          <TabButton active={tab === "edition"} onClick={() => setTab("edition")}>
            <MapPin size={12} /> Edition
          </TabButton>
          <TabButton active={tab === "channel"} onClick={() => setTab("channel")}>
            <Building2 size={12} /> Channel
          </TabButton>
          <div className="flex-1" />
          {row.needs_review && (
            <span
              className="font-mono text-[10px] uppercase tracking-folio px-1.5 py-0.5 mb-1"
              style={{ background: "var(--accent-soft)", color: "var(--accent)" }}
            >
              {parseReviewReasons(row.review_reason).length} review note(s)
            </span>
          )}
        </div>

        {/* Review banner shown on both tabs */}
        {row.needs_review && (
          <ReviewBanner reasons={parseReviewReasons(row.review_reason)} />
        )}

        {tab === "edition" ? (
          <EditionTab
            row={row}
            cityOptions={cityOptions}
            stateOptions={stateOptions}
            cityByKey={cityByKey}
            stateByKey={stateByKey}
            dimsLoaded={dims.isSuccess}
            onSaved={() => {
              qc.invalidateQueries({ queryKey: ["masters/editions"] });
              qc.invalidateQueries({ queryKey: ["masters/dimensions"] });
              qc.invalidateQueries({ queryKey: ["publishers"] });
              onSaved();
            }}
          />
        ) : (
          <ChannelTab
            row={row}
            onSaved={() => {
              qc.invalidateQueries({ queryKey: ["masters/editions"] });
              qc.invalidateQueries({ queryKey: ["masters/dimensions"] });
              qc.invalidateQueries({ queryKey: ["publishers"] });
              onSaved();
            }}
          />
        )}
      </aside>
    </div>
  );
}

/* ────────────────── Tab buttons + banner ───────────────────────────── */
function TabButton({
  active, onClick, children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-3 py-1.5 font-mono text-[11px] uppercase tracking-folio relative inline-flex items-center gap-1.5"
      style={{
        color: active ? "var(--fg)" : "var(--fg-muted)",
        background: active ? "var(--bg-inset)" : "transparent",
      }}
    >
      {children}
      {active && (
        <span aria-hidden className="absolute left-0 right-0 -bottom-[1px] h-[2px]"
              style={{ background: "var(--accent)" }} />
      )}
    </button>
  );
}

function ReviewBanner({ reasons }: { reasons: ReturnType<typeof parseReviewReasons> }) {
  return (
    <div className="mx-5 mt-3 p-3 border-l-4 flex flex-col gap-2"
         style={{
           borderColor: "var(--accent)",
           background: "var(--accent-soft)",
         }}>
      {reasons.map(r => (
        <div key={r.code}>
          <div className="flex items-center gap-1.5">
            <AlertTriangle size={11} className="text-[var(--accent)]" />
            <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--accent)]">
              {r.title}
            </span>
          </div>
          <p className="font-sans text-[12px] text-[var(--fg)] mt-1 leading-snug">{r.body}</p>
          {r.action && (
            <p className="font-sans text-[12px] text-[var(--fg-muted)] mt-1 leading-snug">
              <span className="font-mono text-[10px] uppercase tracking-folio">Action — </span>
              {r.action}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}


/* ────────────────── EDITION tab ─────────────────────────────────────── */
interface EditTabProps {
  row: EditionRow;
  cityOptions: ComboOption[];
  stateOptions: ComboOption[];
  cityByKey: Map<string, CityOption>;
  stateByKey: Map<string, number | null>;
  dimsLoaded: boolean;
  onSaved: () => void;
}

function EditionTab({
  row, cityOptions, stateOptions, cityByKey, stateByKey, dimsLoaded, onSaved,
}: EditTabProps) {
  const [city, setCity]           = useState(row.city ?? "");
  const [cityId, setCityId]       = useState<number | null>(row.city_id);
  const [edition, setEdition]     = useState(row.edition ?? "");
  const [state, setStateVal]      = useState(row.state ?? "");
  const [stateId, setStateId]     = useState<number | null>(row.state_id);
  const [zone, setZone]           = useState(row.zone ?? "");
  const [isActive, setIsActive]   = useState(row.is_active);
  const [isCrawlable, setIsCrawlable] = useState(row.is_crawlable);
  const [circulation, setCirc]    = useState(row.circulation?.toString() ?? "");
  const [readership, setReader]   = useState(row.readership?.toString() ?? "");
  const [clearReview, setClear]   = useState(false);
  const [err, setErr]             = useState<string | null>(null);

  // Auto-fill State / StateId / Zone when the City value resolves exactly to
  // one row in dbo.CityMaster. The user is then free to override — this
  // effect only fires when `city` itself changes (not on every render).
  // We compare against `prevCity` so the effect doesn't trigger on the
  // initial mount with the row's stored values.
  const prevCity = useRef(city);
  useEffect(() => {
    if (city === prevCity.current) return;
    prevCity.current = city;
    const hit = cityByKey.get(city.trim().toLowerCase());
    if (!hit) {
      setCityId(null);   // city no longer linked to a master row
      return;
    }
    setCityId(hit.city_id);
    if (hit.state)     setStateVal(hit.state);
    if (hit.state_id !== undefined) setStateId(hit.state_id ?? null);
    if (hit.zone)      setZone(hit.zone);
  }, [city, cityByKey]);

  // If the user types a State that matches a master row, capture the id.
  useEffect(() => {
    const sid = stateByKey.get(state.trim().toLowerCase());
    if (sid !== undefined) setStateId(sid);
  }, [state, stateByKey]);

  const mut = useMutation({
    mutationFn: () => patchEdition(row.edition_id, {
      city:        city    === (row.city ?? "")    ? undefined : city,
      city_id:     cityId  === row.city_id         ? undefined : cityId,
      edition:     edition === (row.edition ?? "") ? undefined : edition,
      state:       state   === (row.state ?? "")   ? undefined : state,
      state_id:    stateId === row.state_id        ? undefined : stateId,
      zone:        zone    === (row.zone ?? "")    ? undefined : zone,
      is_active:    isActive    !== row.is_active    ? isActive    : undefined,
      is_crawlable: isCrawlable !== row.is_crawlable ? isCrawlable : undefined,
      circulation: circulation === (row.circulation?.toString() ?? "") ? undefined :
                    (circulation === "" ? null : Number(circulation)),
      readership:  readership === (row.readership?.toString()  ?? "") ? undefined :
                    (readership  === "" ? null : Number(readership)),
      review_action: clearReview ? "clear" : undefined,
    }),
    onSuccess: onSaved,
    onError: (e) => setErr(explainError(e)),
  });

  const crawlerWillDoNothing = isCrawlable && !row.has_active_crawler_config;
  const cityLinked = cityId !== null;
  const stateLinked = stateId !== null;

  return (
    <div className="p-5 flex flex-col gap-4">
      {/* Read-only metadata */}
      <dl className="grid grid-cols-2 gap-y-2 gap-x-3 text-[12px]">
        <DRow label="Issues (last 30d)" value={String(row.issues_last_30)} />
        <DRow label="Last issue"
              value={row.last_issue_date ? shortDate(row.last_issue_date) : "—"} />
        <DRow label="Has CrawlerConfig"
              value={row.has_active_crawler_config ? "yes" : "no"} />
        <DRow label="Updated by"
              value={`${row.updated_by ?? "—"}${row.updated_at ? " · " + shortDate(row.updated_at) : ""}`} />
      </dl>

      <div className="np-divider" />

      <Field label="City"
             hint="Bound from dbo.CityMaster. Picking from the list auto-fills State / Zone — you can still override.">
        <Combobox
          value={city}
          onChange={setCity}
          options={cityOptions}
          placeholder={dimsLoaded ? "City name" : "Loading cities…"}
          searchHint
          ariaLabel="City"
        />
        <LinkBadge linked={cityLinked} unlinkedText="not in master — will save as free text" />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="State" hint="Search 'kar' → Karnataka.">
          <Combobox
            value={state}
            onChange={setStateVal}
            options={stateOptions}
            placeholder={dimsLoaded ? "State name" : "Loading…"}
            ariaLabel="State"
          />
          <LinkBadge linked={stateLinked} unlinkedText="free text" />
        </Field>
        <Field label="Zone" hint="North / South / East / West / North East. Auto-filled from CityMaster.">
          <input value={zone} onChange={(e) => setZone(e.target.value)}
                 placeholder="—"
                 className="np-input font-mono text-[14px]" />
        </Field>
      </div>

      <div className="np-card-inset px-3 py-2 flex items-start gap-2"
           style={{ background: "var(--bg-inset)" }}>
        <Info size={11} className="text-[var(--fg-faint)] shrink-0 mt-0.5" />
        <div className="font-mono text-[10px] text-[var(--fg-muted)] leading-snug">
          Channel: <strong className="font-display text-[12px] text-[var(--fg)]">{row.channel_name}</strong>
          <span className="text-[var(--fg-faint)]"> · ch{row.channel_id}</span>
          <br />
          To rename the publisher across all editions, use the <em>Channel</em> tab.
        </div>
      </div>

      <Field
        label="Edition · printed label for this row only"
        hint="The publisher's masthead designation for this specific edition (e.g. 'Mumbai', 'West Edition', 'Section II'). Editing here does NOT rename the channel."
      >
        <input value={edition} onChange={(e) => setEdition(e.target.value)}
               className="np-input font-mono text-[14px]" />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Circulation">
          <input type="number" min={0} value={circulation}
                 onChange={(e) => setCirc(e.target.value)}
                 placeholder="—" className="np-input font-mono text-[14px]" />
        </Field>
        <Field label="Readership">
          <input type="number" min={0} value={readership}
                 onChange={(e) => setReader(e.target.value)}
                 placeholder="—" className="np-input font-mono text-[14px]" />
        </Field>
      </div>

      <div className="np-divider" />

      {/* Flags with rich help text */}
      <BoolRow
        label="Active in catalog"
        value={isActive}
        onChange={setIsActive}
        help="When OFF, this edition is hidden from Browse and the dashboard. Use to soft-delete defunct editions without losing history."
      />

      <BoolRow
        label="Crawler enabled"
        value={isCrawlable}
        onChange={setIsCrawlable}
        help={
          row.has_active_crawler_config
            ? "When ON, the daily crawler runner will attempt this edition. A CrawlerConfig row is already wired."
            : "When ON, the daily runner will attempt this edition — but no CrawlerConfig is wired yet, so nothing will happen. Set the URL templates first under Crawler Ops · Masters · CrawlerConfig."
        }
        warning={crawlerWillDoNothing
          ? "Toggle has no effect until a CrawlerConfig row is created for this edition."
          : null}
      />

      {row.needs_review && (
        <label className="np-card-inset px-3 py-2 inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={clearReview}
                 onChange={(e) => setClear(e.target.checked)}
                 className="accent-[var(--accent)]" />
          <ShieldCheck size={12} className="text-[var(--primary)]" />
          <span className="font-mono text-[11px] uppercase tracking-folio">Clear review flag on save</span>
        </label>
      )}

      {err && (
        <div className="np-banner">
          <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--accent)] mr-2">Error</span>
          {err}
        </div>
      )}

      <div className="flex items-center gap-2 justify-end mt-2 sticky bottom-0 bg-[var(--bg-card)] py-2">
        <button onClick={() => mut.reset()} className="np-btn-ghost">Reset</button>
        <button onClick={() => { setErr(null); mut.mutate(); }}
                disabled={mut.isPending}
                className="np-btn">
          {mut.isPending ? "Saving…" : <><Save size={11} /> Save edition</>}
        </button>
      </div>
    </div>
  );
}


/* ────────────────── CHANNEL tab ─────────────────────────────────────── */
function ChannelTab({
  row, onSaved,
}: {
  row: EditionRow;
  onSaved: () => void;
}) {
  const [name, setName]         = useState(row.channel_name);
  const [catId, setCatId]       = useState<number | "">(row.category_id ?? "");
  const [langId, setLangId]     = useState<number | "">(row.language_id ?? "");
  const [isDeleted, setDeleted] = useState<boolean>(!!row.channel_is_deleted);
  const [clearReview, setClear] = useState(false);
  const [err, setErr]           = useState<string | null>(null);
  const [confirmPreview, setConfirmPreview] = useState<ChannelPreviewT | null>(null);

  const dims = useQuery({
    queryKey: ["masters/dimensions"],
    queryFn:  fetchMastersDimensions,
    staleTime: 5 * 60_000,
  });

  const computeBody = (): ChannelPatchBody => ({
    channel_name: name !== row.channel_name ? name : undefined,
    category_id:  catId  !== "" && catId  !== row.category_id  ? Number(catId)  : undefined,
    language_id:  langId !== "" && langId !== row.language_id  ? Number(langId) : undefined,
    is_deleted:   isDeleted !== !!row.channel_is_deleted ? isDeleted : undefined,
    review_action: clearReview ? "clear" : undefined,
  });

  const mut = useMutation({
    mutationFn: () => patchChannel(row.channel_id, computeBody()),
    onSuccess: () => { setConfirmPreview(null); onSaved(); },
    onError: (e) => setErr(explainError(e)),
  });

  /** Decide whether the change requires confirmation.
   *  Rename → always confirm. Category change → confirm (propagates to all eds).
   *  Language change → confirm. Soft-delete → confirm. Clear-review only → no.
   */
  const needsConfirm = () => {
    const b = computeBody();
    return Boolean(b.channel_name || b.category_id !== undefined ||
                   b.language_id !== undefined || b.is_deleted !== undefined);
  };

  const onSaveClicked = async () => {
    setErr(null);
    const b = computeBody();
    if (Object.values(b).every(v => v === undefined)) {
      setErr("Nothing to save");
      return;
    }
    if (needsConfirm()) {
      try {
        const preview = await fetchChannelPreview(row.channel_id);
        setConfirmPreview(preview);
      } catch (e) {
        setErr(explainError(e));
      }
      return;
    }
    mut.mutate();
  };

  return (
    <div className="p-5 flex flex-col gap-4">
      <div className="np-banner flex items-baseline gap-2">
        <Info size={12} className="text-[var(--primary)] shrink-0" />
        <div className="font-sans text-[12px] text-[var(--fg-muted)] leading-snug">
          Changes on this tab update <code className="font-mono">dbo.Channel</code> AND
          propagate to <strong>every edition row</strong> of this channel
          (denormalised Category / Language). One transaction.
        </div>
      </div>

      <Field label="Channel name" hint="Renames the publisher across the entire catalog.">
        <input value={name} onChange={(e) => setName(e.target.value)}
               className="np-input font-display text-[18px]" />
      </Field>

      <Field label="Category" hint="One of: Regional, Mainline, Financial, Periodical.">
        <select value={String(catId)} onChange={(e) => setCatId(e.target.value ? Number(e.target.value) : "")}
                className="np-input font-mono text-[14px]">
          <option value="">— pick a category —</option>
          {(dims.data?.categories ?? []).map(c => (
            <option key={c.id} value={c.id}>{c.name} ({c.edition_count} editions)</option>
          ))}
        </select>
      </Field>

      <Field label="Language">
        <select value={String(langId)} onChange={(e) => setLangId(e.target.value ? Number(e.target.value) : "")}
                className="np-input font-mono text-[14px]">
          <option value="">— pick a language —</option>
          {(dims.data?.languages ?? []).map(l => (
            <option key={l.id} value={l.id}>{l.name}{l.iso_code ? ` (${l.iso_code})` : ""} — {l.edition_count}</option>
          ))}
        </select>
      </Field>

      <BoolRow
        label="Channel deleted (soft-delete in dbo.Channel)"
        value={isDeleted}
        onChange={setDeleted}
        help="Marks dbo.Channel.IsDeleted=1. Doesn't remove rows. Use when the publisher has shut down or is a duplicate of another channel."
      />

      {row.needs_review && (
        <label className="np-card-inset px-3 py-2 inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={clearReview}
                 onChange={(e) => setClear(e.target.checked)}
                 className="accent-[var(--accent)]" />
          <ShieldCheck size={12} className="text-[var(--primary)]" />
          <span className="font-mono text-[11px] uppercase tracking-folio">Clear review flag for every edition of this channel</span>
        </label>
      )}

      {err && (
        <div className="np-banner">
          <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--accent)] mr-2">Error</span>
          {err}
        </div>
      )}

      <div className="flex items-center gap-2 justify-end mt-2 sticky bottom-0 bg-[var(--bg-card)] py-2">
        <button onClick={onSaveClicked}
                disabled={mut.isPending}
                className="np-btn">
          {mut.isPending ? "Saving…" : <><Save size={11} /> Save channel</>}
        </button>
      </div>

      {confirmPreview && (
        <ChannelChangeConfirm
          preview={confirmPreview}
          newName={name !== row.channel_name ? name : null}
          newCategory={catId !== "" && catId !== row.category_id
                       ? (dims.data?.categories.find(c => c.id === catId)?.name ?? null)
                       : null}
          newLanguage={langId !== "" && langId !== row.language_id
                       ? (dims.data?.languages.find(l => l.id === langId)?.name ?? null)
                       : null}
          softDelete={isDeleted !== !!row.channel_is_deleted ? isDeleted : null}
          pending={mut.isPending}
          onCancel={() => setConfirmPreview(null)}
          onConfirm={() => mut.mutate()}
        />
      )}
    </div>
  );
}


/* ────────────────── Channel-change confirmation modal ──────────────── */
function ChannelChangeConfirm({
  preview, newName, newCategory, newLanguage, softDelete,
  pending, onCancel, onConfirm,
}: {
  preview: ChannelPreviewT;
  newName: string | null;
  newCategory: string | null;
  newLanguage: string | null;
  softDelete: boolean | null;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  // Esc-to-cancel.
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onCancel]);

  const editions    = preview.editions ?? [];
  const activeEds   = editions.filter(e => e.is_active);
  const inactiveEds = editions.filter(e => !e.is_active);
  const zeroEds     = editions.length === 0;

  // Rendered at document.body via a portal so we escape the drawer's
  // stacking + overflow context. Without this, the modal renders inside
  // the 520-px aside and either gets clipped or visually merges with it.
  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      role="dialog" aria-modal="true" aria-labelledby="channel-confirm-title"
      style={{ background: "rgba(8,10,15,0.62)" }}
    >
      <div
        onClick={onCancel}
        className="absolute inset-0"
        aria-hidden
      />
      <div
        className="relative w-full max-w-[560px] flex flex-col"
        style={{
          background:    "var(--bg-card)",
          color:         "var(--fg)",
          border:        "1px solid var(--rule-strong)",
          boxShadow:     "0 24px 64px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.04)",
          maxHeight:     "85vh",
        }}
      >
        <div className="px-5 py-3 flex items-center gap-2"
             style={{ borderBottom: "1px solid var(--rule)" }}>
          <AlertTriangle size={14} className="text-[var(--accent)]" />
          <h3 id="channel-confirm-title" className="font-display text-[18px] leading-none">
            Confirm channel change
          </h3>
          <button onClick={onCancel} aria-label="Close"
                  className="ml-auto np-btn-ghost p-1.5"
                  disabled={pending}>
            <XIcon size={12} />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-3 overflow-auto" style={{ flex: 1 }}>
          <p className="font-sans text-[13px] leading-snug" style={{ color: "var(--fg)" }}>
            You are about to change a <strong>channel-level</strong> field on{" "}
            <code className="font-mono">{preview.channel_name}</code>{" "}
            <span className="text-[var(--fg-faint)]">(ch{preview.channel_id})</span>.{" "}
            {zeroEds
              ? <>This channel has <strong>no edition rows</strong> yet, so only <code className="font-mono">dbo.Channel</code> will change.</>
              : <>This propagates to every edition row below in <strong>one transaction</strong>.</>
            }
          </p>

          <div className="np-card-inset px-3 py-2.5 flex flex-col gap-1.5">
            {newName !== null && (
              <ChangeRow label="Rename"
                         from={preview.channel_name} to={newName} />
            )}
            {newCategory !== null && (
              <ChangeRow label="Category"
                         from={preview.category ?? "—"} to={newCategory} />
            )}
            {newLanguage !== null && (
              <ChangeRow label="Language"
                         from={preview.language ?? "—"} to={newLanguage} />
            )}
            {softDelete !== null && (
              <ChangeRow label="Soft-delete"
                         from={preview.is_deleted ? "yes" : "no"}
                         to={softDelete ? "yes" : "no"} />
            )}
          </div>

          <div className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)]">
            Affects {preview.edition_count} edition{preview.edition_count === 1 ? "" : "s"}
            {inactiveEds.length > 0 && (
              <> · {activeEds.length} active · {inactiveEds.length} inactive</>
            )}
          </div>

          {zeroEds ? (
            <div className="np-card-inset px-3 py-3 font-mono text-[11px] text-[var(--fg-faint)] text-center">
              (no editions wired to this channel yet)
            </div>
          ) : (
            <ul className="np-card-inset divide-y" style={{ borderColor: "var(--rule)" }}>
              {editions.slice(0, 40).map(e => (
                <li key={e.edition_id} className="px-3 py-1.5 flex items-baseline gap-2"
                    style={{ borderColor: "var(--rule)",
                             opacity: e.is_active ? 1 : 0.55 }}>
                  <span className="font-mono text-[10px] text-[var(--fg-faint)] tabular-nums w-12">
                    #{e.edition_id}
                  </span>
                  <span className="font-display text-[13px] truncate">
                    {e.city ?? <em className="text-[var(--accent)]">no city</em>}
                  </span>
                  {e.edition && e.edition !== e.city && (
                    <span className="font-mono text-[10px] text-[var(--fg-faint)] ml-auto truncate">
                      {e.edition}
                    </span>
                  )}
                  {!e.is_active && (
                    <span className="font-mono text-[9px] uppercase tracking-folio text-[var(--fg-faint)] ml-auto">
                      inactive
                    </span>
                  )}
                </li>
              ))}
              {editions.length > 40 && (
                <li className="px-3 py-1.5 font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)]">
                  + {editions.length - 40} more
                </li>
              )}
            </ul>
          )}
        </div>

        <div className="px-5 py-3 flex items-center justify-end gap-2"
             style={{ borderTop: "1px solid var(--rule)" }}>
          <button onClick={onCancel} className="np-btn-ghost" disabled={pending}>Cancel</button>
          <button onClick={onConfirm} className="np-btn" disabled={pending}>
            {pending
              ? "Applying…"
              : zeroEds
                ? <>Apply to channel only</>
                : <>Apply to {preview.edition_count} edition{preview.edition_count === 1 ? "" : "s"}</>}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ChangeRow({ label, from, to }: { label: string; from: string; to: string }) {
  return (
    <div className="flex items-baseline gap-2 text-[12px]">
      <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)] w-24 shrink-0">{label}</span>
      <span className="font-mono text-[12px] text-[var(--fg-muted)] line-through truncate">{from}</span>
      <span className="font-mono text-[10px] text-[var(--fg-faint)]">→</span>
      <span className="font-display text-[13px] truncate">{to}</span>
    </div>
  );
}


/* ────────────────── tiny presentational helpers ─────────────────────── */
function Field({
  label, hint, children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="np-label">{label}</span>
      {children}
      {hint && (
        <span className="font-mono text-[10px] text-[var(--fg-faint)] leading-snug">{hint}</span>
      )}
    </label>
  );
}

function DRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)]">{label}</dt>
      <dd className="font-mono text-[11px] text-[var(--fg-muted)] truncate" title={value}>{value}</dd>
    </>
  );
}

function LinkBadge({
  linked, unlinkedText,
}: {
  linked: boolean;
  unlinkedText: string;
}) {
  // Linked = matches a master row exactly. We show the warning only when
  // unlinked — when everything is fine the field stays quiet.
  if (linked) return null;
  return (
    <span className="font-mono text-[10px] mt-1"
          style={{ color: "var(--accent)" }}>
      ○ {unlinkedText}
    </span>
  );
}

function BoolRow({
  label, value, onChange, help, warning,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  help?: string;
  warning?: string | null;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="np-card-inset px-3 py-2 inline-flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={value}
               onChange={(e) => onChange(e.target.checked)}
               className="accent-[var(--accent)]" />
        <span className="font-mono text-[11px] uppercase tracking-folio">{label}</span>
        <span className="ml-auto font-mono text-[10px] text-[var(--fg-faint)]">
          {value ? "yes" : "no"}
        </span>
      </label>
      {help && (
        <span className="font-mono text-[10px] text-[var(--fg-faint)] leading-snug pl-1">{help}</span>
      )}
      {warning && (
        <div className="px-2 py-1.5 inline-flex items-center gap-1.5"
             style={{ background: "var(--accent-soft)" }}>
          <AlertTriangle size={11} className="text-[var(--accent)] shrink-0" />
          <span className="font-mono text-[10px] text-[var(--accent)] leading-snug">{warning}</span>
        </div>
      )}
    </div>
  );
}
